import Vue from "vue";
import moment from "moment";
import "moment-timezone";
import * as BankService from "@/shared/api/bankService";
import * as FileContributionService from "@/shared/api/fileContributionService";
import {
    FetchState,
    ListParams,
    AchContributionState,
    AchContributionRecord
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import { downloadCsvFile } from "@/shared/fileDownload";
import { defineStore } from "pinia";
import _ from "lodash";
import { useUserStore } from "@/shared/store/user";

const pageSize = 10;

export const initialState: AchContributionState = {
    fetchState: FetchState.Uninitialized,
    pageNumber: 1,
    totalRecords: 0,
    records: [],
    pages: {},
    selectedAchId: null,
    selectedPayrollDate: null
};

export const useAchContributionStore = defineStore("achContribution", {
    state: (): AchContributionState => (_.cloneDeep(initialState)),
    getters: {
        achRecordsLoaded: state => state.fetchState === FetchState.Loaded,
        pageCount () {
            return this.totalRecords === 0 ? 1 : Math.ceil(this.totalRecords / pageSize);
        },
        achRecordsByPageNumber: state => {
            const pageObject = state.pages[state.pageNumber];

            if (typeof pageObject === "undefined") {
                return [];
            }
            return pageObject;
        },
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setAchContributionRecords (records: AchContributionRecord[]) {
            this.records = [ ...this.records, ...records.map(mapRecordsForDisplay) ];
            Vue.set(this.pages, this.pageNumber, records.map(mapRecordsForDisplay));
        },
        setTotalRecords (totalRecords: number) {
            this.totalRecords = totalRecords;
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        setSelectedFile (file: AchContributionRecord) {
            this.selectedAchId = file.employerAchId;
            this.selectedPayrollDate = file.payrollDate;
        },
        loadRecords ({ pageNumber }: ListParams) {
            this.setPageNumber(pageNumber || 1);
            const pageObject = this.pages[pageNumber || 1];
            if (this.fetchState === FetchState.Loading || typeof pageObject !== "undefined") {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);
            let achRecords = [];
            const userStore = useUserStore();
            return BankService.getAchContributionTotalRecords(userStore.employerId)
                .then(result => this.setTotalRecords(result.data))
                .then(() => BankService.getAchContributions(userStore.employerId, pageNumber, pageSize))
                .then(result => {
                    achRecords = result.data.records;
                    return achRecords;
                })
                .then(result =>
                    FileContributionService
                        .getProcessedHoldOverAmountsByPayDates(userStore.employerId,
                            result.map(a => a.payrollDate))
                )
                .then(res => {
                    const holdOverRecords = res.data.records;
                    holdOverRecords.forEach(element => {
                        const record = achRecords.find(e => e.payrollDate === element.processedDate);
                        if (record) {

                            if (element.achDetails) {
                                element.achDetails.forEach(x => {
                                    x.processDate = moment(x.processDate).format("MM/DD/YYYY");
                                });
                            }

                            record.hasHoldOverAmount = !!element.holdAmount;
                            record.holdoverAmount = element.holdAmount;
                            record.achDetails = element.achDetails;
                            record.hasMultiple = element.hasMultiple;
                        }
                    });
                    this.setFetchState(FetchState.Loaded);
                    this.setAchContributionRecords(achRecords);
                })
                .catch(error => storeHelper.handleError({ error, rethrow: false }));
        },
        downloadEmployeeDisbursalReport (achReportId: number): Promise<any> {
            const userStore = useUserStore();
            return BankService.downloadEmployeeDisbursalReport(userStore.employerId, achReportId)
                .then(response => {
                    const fileName = `employee-disbursal-${achReportId}-${moment().format("MM.DD.YYYY")}.csv`;
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        downloadProcessedEmployeeHoldOverReport (payrollDate: string): Promise<any> {
            const userStore = useUserStore();
            return FileContributionService.downloadEmployeeProcessedHoldOverReport(userStore.employerId,
                    moment(payrollDate).format("YYYY-MM-DD"))
                .then(response => {
                    const fileName = "Processed-Employee-PendingContributions_" +
                        moment(payrollDate).format("MM.DD.YYYY") + ".csv";
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
    }
});

function mapRecordsForDisplay (record: AchContributionRecord): AchContributionRecord {
    if (record.achDetails) {
        record.achDetails.forEach(x => {
            x.processDate = moment(x.processDate).format("MM/DD/YYYY");
        });
    }

    return {
        ...record,
        payrollDate: moment(record.payrollDate).format("MM/DD/YYYY"),
        achDate: moment(record.achDate).format("MM/DD/YYYY"),
        hasHoldOverAmount: record.hasHoldOverAmount || false
    };
}